import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Loader2, Save, FileText, ChevronDown, ChevronRight,
  Plus, Trash2, ChevronUp, Upload, X, Image as ImageIcon,
  Film,
  Home, Globe, BookOpen, DollarSign, Users,
  HelpCircle, Calendar, GraduationCap, Layout, Star, Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { clearCmsCache } from '../../hooks/useCmsContent';
import { GENERIC_AVATARS } from '../../data/genericAvatars';
import { SUPPORTED_LANGUAGES } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CONTENT_REQUEST_TIMEOUT_MS = 20000;

const buildRequestError = (err, endpoint, fallback) => {
  const status = err.response?.status;
  const detail = err.response?.data?.detail || err.message || fallback;
  return {
    status,
    endpoint,
    message: `${fallback}${status ? ` (${status})` : ''}: ${detail}`,
  };
};

// ─── CMS STRUCTURE ─────────────────────────────────────────────────────────────
const getDefaultSectionContent = (section) => {
  if (!section) return {};
  if (section.type === 'repeater') {
    return { items: section.defaultItems || [] };
  }
  return (section.fields || []).reduce((content, field) => {
    if (field.defaultValue !== undefined) {
      content[field.key] = field.defaultValue;
    }
    return content;
  }, {});
};

const CMS_STRUCTURE = [
  {
    slug: 'header', label: 'Header & Navigation', icon: Layout,
    sections: [
      { key: 'logo', label: 'Logo', fields: [
        { key: 'logo_url', label: 'Logo Image', type: 'image', placeholder: '/logo.png', defaultValue: '/logo.png' },
      ]},
      { key: 'nav', label: 'Navigation Links', type: 'repeater', itemFields: [
        { key: 'label', label: 'Link Label', type: 'text', placeholder: 'Home' },
        { key: 'path',  label: 'Path / URL',  type: 'text', placeholder: '/' },
      ], defaultItems: [
        { label: 'Home',       path: '/' },
        { label: 'Courses',    path: '/courses' },
        { label: 'Pricing',    path: '/pricing' },
        { label: 'Level Test', path: '/level-test' },
        { label: 'About',      path: '/about' },
        { label: 'Contact',    path: '/contact' },
      ]},
      { key: 'cta', label: 'Header CTA Buttons', fields: [
        { key: 'primary_label',   label: 'Primary Button Text',   type: 'text', placeholder: 'Test Your Level', defaultValue: 'Test Your Level' },
        { key: 'primary_link',    label: 'Primary Button Link',   type: 'text', placeholder: '/level-test',     defaultValue: '/level-test' },
        { key: 'secondary_label', label: 'Secondary Button Text', type: 'text', placeholder: 'Book a Trial',    defaultValue: 'Book a Trial' },
        { key: 'secondary_link',  label: 'Secondary Button Link', type: 'text', placeholder: '/book-trial',     defaultValue: '/book-trial' },
      ]},
    ]
  },
  {
    slug: 'footer', label: 'Footer', icon: Globe,
    sections: [
      { key: 'info', label: 'Description & Logo', fields: [
        { key: 'logo_url',    label: 'Footer Logo',      type: 'image',    placeholder: '/logo.png',                             defaultValue: '/logo.png' },
        { key: 'description', label: 'Description Text', type: 'textarea', placeholder: 'Personalized English lessons...' },
        { key: 'location',    label: 'Location Text',    type: 'text',     placeholder: 'Online Worldwide · In-person available on request', defaultValue: 'Online Worldwide · In-person classes available on request' },
      ]},
      { key: 'quick_links', label: 'Quick Links', type: 'repeater', itemFields: [
        { key: 'label', label: 'Link Label', type: 'text', placeholder: 'Home' },
        { key: 'path',  label: 'Path / URL', type: 'text', placeholder: '/' },
      ], defaultItems: [
        { label: 'Home',    path: '/' },
        { label: 'Courses', path: '/courses' },
        { label: 'Pricing', path: '/pricing' },
        { label: 'About',   path: '/about' },
      ]},
      { key: 'support_links', label: 'Support Links', type: 'repeater', itemFields: [
        { key: 'label', label: 'Link Label', type: 'text', placeholder: 'FAQ' },
        { key: 'path',  label: 'Path / URL', type: 'text', placeholder: '/faq' },
      ], defaultItems: [
        { label: 'FAQ',          path: '/faq' },
        { label: 'Contact',      path: '/contact' },
        { label: 'Level Test',   path: '/level-test' },
        { label: 'Book a Trial', path: '/book-trial' },
      ]},
    ]
  },
  {
    slug: 'home', label: 'Home Page', icon: Home,
    sections: [
      { key: 'hero', label: 'Hero Section', fields: [
        { key: 'badge',         label: 'Badge Text',       type: 'text',     placeholder: 'CERTIFIED CELTA & TESOL TEACHERS.',   defaultValue: 'CERTIFIED CELTA & TESOL TEACHERS.' },
        { key: 'brand',         label: 'Brand Line',      type: 'text',     placeholder: 'FRANKLY TALKING',                    defaultValue: 'FRANKLY TALKING' },
        { key: 'title',         label: 'Headline',         type: 'text',     placeholder: 'Speak English with Confidence',     defaultValue: 'Speak English with Confidence' },
        { key: 'subtitle',      label: 'Subtitle',         type: 'textarea', placeholder: 'Personalised online lessons…',       defaultValue: 'Personalised English lessons for real-life communication, international opportunities and confident speaking.' },
        { key: 'cta_primary',   label: 'Primary Button',   type: 'text',     placeholder: 'Test Your Level',                    defaultValue: 'Test Your Level' },
        { key: 'cta_secondary', label: 'Secondary Button', type: 'text',     placeholder: 'Book a Trial',                      defaultValue: 'Book a Trial' },
        { key: 'image',         label: 'Hero Image (fallback/poster)', type: 'image', defaultValue: '/assets/photos/site-updates/hero-cida.webp' },
        { key: 'video',         label: 'Hero Video (MP4/WebM)',        type: 'video', defaultValue: '/assets/videos/hero-teacher.mp4' },
        { key: 'video_poster',  label: 'Hero Video Poster',            type: 'image', defaultValue: '/assets/photos/site-updates/hero-final-touches.webp' },
      ]},
      { key: 'features_header', label: 'Why Choose Us — Header', fields: [
        { key: 'badge',    label: 'Badge',          type: 'text',     placeholder: 'Why Choose Us',                  defaultValue: 'Why Choose Us' },
        { key: 'title',    label: 'Headline',       type: 'text',     placeholder: 'Learn English with Confidence',  defaultValue: 'Learn English with [hl]Confidence[/hl]' },
        { key: 'body',     label: 'Paragraph 1',    type: 'textarea',                                                defaultValue: 'Learn English with confidence, guided by a highly qualified and internationally experienced teacher. You will be taught by a native Portuguese-speaking instructor with extensive global experience in English language teaching, fully certified by the University of Cambridge.' },
        { key: 'body2',    label: 'Paragraph 2',    type: 'textarea',                                                defaultValue: "With a Master's degree and ongoing doctoral research in English Language Teaching methodology, our approach combines academic excellence with practical, real-world communication skills to help you achieve fluency faster and more effectively." },
        { key: 'closing',  label: 'Closing Line',         type: 'text', placeholder: 'Start your journey to fluent English today!', defaultValue: 'Start your journey to fluent English today.' },
        { key: 'subtitle', label: 'Differentiators Label', type: 'text', placeholder: 'What makes us different:',                  defaultValue: 'What makes us different:' },
      ]},
      { key: 'features', label: 'Why Choose Us — Bullets', type: 'repeater', itemFields: [
        { key: 'title',       label: 'Title',       type: 'text',     placeholder: 'International Teaching Experience' },
        { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Extensive global experience…' },
      ], defaultItems: [
        { title: 'International Teaching Experience',          description: 'Experience teaching students from different countries, cultures and learning backgrounds.' },
        { title: 'Cambridge-Certified Expertise', description: 'Cambridge-certified expertise with advanced academic training in English Language Teaching.' },
        { title: "Master's Degree + PhD-Level Specialisation", description: "Academic excellence combining a Master's degree with ongoing doctoral research in English Language Teaching methodology." },
        { title: 'Clear Explanations for Portuguese Speakers', description: 'A native Portuguese-speaking teacher who understands your challenges and explains English clearly.' },
        { title: 'Real Results', description: 'Practical lessons designed to build confidence in real conversations.' },
      ]},
      { key: 'community', label: 'Community Section', fields: [
        { key: 'badge',  label: 'Badge',       type: 'text',     placeholder: 'Global Community',                    defaultValue: 'Global Community' },
        { key: 'title',  label: 'Title',       type: 'text',     placeholder: 'Join Learners From Around the World', defaultValue: 'Join Learners From [hl]Around the World[/hl]' },
        { key: 'body',   label: 'Description', type: 'textarea',                                                      defaultValue: "Our students come from diverse backgrounds and cultures, united by the goal of confident English communication. You'll join a supportive community of learners from Latin America, Europe, Asia, and beyond." },
        { key: 'stat',   label: 'Stat Text',   type: 'text',     placeholder: '500+ happy students worldwide',       defaultValue: '500+ happy students worldwide' },
        { key: 'image',  label: 'Primary Image',   type: 'image', defaultValue: '/assets/photos/site-updates/online-worldwide-working.webp' },
        { key: 'image2', label: 'Secondary Image', type: 'image', defaultValue: '/assets/photos/site-updates/aviation.webp' },
        { key: 'image3', label: 'Tertiary Image',  type: 'image', defaultValue: '/assets/photos/site-updates/advanced-reading-strategies.webp' },
        { key: 'person1_avatar', label: 'Avatar 1', type: 'image', defaultValue: GENERIC_AVATARS[0] },
        { key: 'person2_avatar', label: 'Avatar 2', type: 'image', defaultValue: GENERIC_AVATARS[1] },
        { key: 'person3_avatar', label: 'Avatar 3', type: 'image', defaultValue: GENERIC_AVATARS[2] },
        { key: 'person4_avatar', label: 'Avatar 4', type: 'image', defaultValue: GENERIC_AVATARS[3] },
      ]},
      { key: 'how_it_works', label: 'How It Works Header', fields: [
        { key: 'badge',    label: 'Badge',    type: 'text',     placeholder: 'Getting Started',                  defaultValue: 'Getting Started' },
        { key: 'title',    label: 'Title',    type: 'text',     placeholder: 'How It Works',                     defaultValue: 'How It Works' },
        { key: 'subtitle', label: 'Subtitle', type: 'textarea', placeholder: 'A simple journey…',                defaultValue: 'A simple journey from your first step to confident communication.' },
      ]},
      { key: 'steps', label: 'Steps', type: 'repeater', itemFields: [
        { key: 'number',      label: 'Step Number', type: 'text',     placeholder: '01' },
        { key: 'title',       label: 'Title',       type: 'text',     placeholder: 'Take the Level Test' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ], defaultItems: [
        { number: '01', title: 'Test Your Level',             description: 'Find out your current level through a quick and practical assessment.' },
        { number: '02', title: 'Get Your Recommended Plan', description: 'We suggest the package that best fits your level and learning goals.' },
        { number: '03', title: 'Book and Start Learning',   description: 'Choose your package, schedule your lessons, and begin learning with confidence.' },
      ]},
      { key: 'programs', label: 'Programs Header', fields: [
        { key: 'badge',    label: 'Badge',           type: 'text',     placeholder: 'Our Courses',      defaultValue: 'Our Courses' },
        { key: 'title',    label: 'Title',           type: 'text',     placeholder: 'Popular Programs', defaultValue: 'Popular Programs' },
        { key: 'subtitle', label: 'Subtitle',        type: 'textarea',                                  defaultValue: 'Flexible learning options designed for real goals and real progress.' },
        { key: 'cta',      label: 'View All Button', type: 'text',     placeholder: 'View All Courses', defaultValue: 'View All Courses' },
      ]},
      { key: 'program_cards', label: 'Program Cards', type: 'repeater', itemFields: [
        { key: 'title',       label: 'Title',       type: 'text',     placeholder: 'General English' },
        { key: 'subtitle',    label: 'Subtitle',    type: 'text',     placeholder: 'From Beginners to Advanced' },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'button',      label: 'Button Text', type: 'text',     placeholder: 'Learn More' },
      ], defaultItems: [
        { title: 'General English',  subtitle: 'From Beginners to Advanced',    description: 'Build confidence in speaking, listening, reading, writing, grammar, and pronunciation.', button: 'Learn More' },
        { title: 'Business English', subtitle: 'Professional Communication',    description: 'Communicate more professionally and confidently in meetings, emails, presentations, and interviews.', button: 'Learn More' },
        { title: 'Academic English', subtitle: 'English for Academic Purposes', description: 'Prepare for essays, lectures, presentations, note-taking, and academic success.', button: 'Learn More' },
      ]},
      { key: 'testimonials_header', label: 'Testimonials Header', fields: [
        { key: 'badge',    label: 'Badge',    type: 'text',     placeholder: 'Testimonials',          defaultValue: 'Testimonials' },
        { key: 'title',    label: 'Title',    type: 'text',     placeholder: 'What Our Students Say', defaultValue: 'What Our Students Say' },
        { key: 'subtitle', label: 'Subtitle', type: 'textarea',                                        defaultValue: 'Real stories from our global community of learners.' },
      ]},
      { key: 'cta', label: 'Bottom CTA', fields: [
        { key: 'title',         label: 'Title',            type: 'text',     placeholder: 'Ready to Start Your English Journey?', defaultValue: 'Ready to Start Your [hl]English Journey[/hl]?' },
        { key: 'body',          label: 'Description',      type: 'textarea',                                                       defaultValue: 'Take your free level test or book a trial lesson and discover the right learning path for your goals.' },
        { key: 'cta_primary',   label: 'Primary Button',   type: 'text',     placeholder: 'Test Your Level',                      defaultValue: 'Test Your Level' },
        { key: 'cta_secondary', label: 'Secondary Button', type: 'text',     placeholder: 'Book a Trial',                         defaultValue: 'Book a Trial' },
      ]},
    ]
  },
  {
    slug: 'about', label: 'About Page', icon: Users,
    sections: [
      { key: 'hero', label: 'Hero / Our Story', fields: [
        { key: 'badge',  label: 'Badge',       type: 'text',     placeholder: 'About Us',  defaultValue: 'About Us' },
        { key: 'title',  label: 'Title',       type: 'text',     placeholder: 'Our Story', defaultValue: 'Our Story' },
        { key: 'body1',  label: 'Paragraph 1', type: 'textarea',                            defaultValue: 'Frankly Talking English was created with a passion for teaching and a commitment to real communication. With years of experience, certified methodology, and a personalized approach, we help learners speak more clearly, confidently, and naturally.' },
        { key: 'body2',  label: 'Paragraph 2', type: 'textarea',                            defaultValue: 'We believe that every student deserves high-quality, engaging, and practical English education. Our mission is to break down language barriers and help people connect with the world through confident communication.' },
        { key: 'stat',   label: 'Stat Text',   type: 'text',     placeholder: '500+ students from 30+ countries', defaultValue: '500+ students from 30+ countries' },
        { key: 'image',  label: 'Hero Image',  type: 'image',                               defaultValue: '/assets/photos/site-updates/pronunciation.webp' },
        { key: 'person1_avatar', label: 'Avatar 1', type: 'image', defaultValue: GENERIC_AVATARS[0] },
        { key: 'person2_avatar', label: 'Avatar 2', type: 'image', defaultValue: GENERIC_AVATARS[1] },
        { key: 'person3_avatar', label: 'Avatar 3', type: 'image', defaultValue: GENERIC_AVATARS[2] },
      ]},
      { key: 'differentiators', label: 'Differentiators List', type: 'repeater', itemFields: [
        { key: 'text', label: 'Item Text', type: 'text', placeholder: 'Personalized learning paths' },
      ], defaultItems: [
        { text: 'Personalized learning paths' },
        { text: 'Real communication focus' },
        { text: 'Certified and experienced teaching' },
        { text: 'Flexible learning experience' },
        { text: 'Clear progress support' },
        { text: 'Warm, confidence-building environment' },
      ]},
      { key: 'methodology', label: 'Teaching Methodology', fields: [
        { key: 'badge',  label: 'Badge',       type: 'text',     placeholder: 'Our Approach',             defaultValue: 'Our Approach' },
        { key: 'title',  label: 'Title',       type: 'text',     placeholder: 'Our Teaching Methodology', defaultValue: 'Our Teaching Methodology' },
        { key: 'body',   label: 'Description', type: 'textarea',                                           defaultValue: 'Our lessons are designed to balance structure, communication, and personalization. We believe students learn better when lessons feel practical, engaging, and connected to real life.' },
        { key: 'image',  label: 'Image',       type: 'image',                                              defaultValue: '/assets/photos/site-updates/speaking-development.webp' },
      ]},
      { key: 'methodology_points', label: 'Methodology Points', type: 'repeater', itemFields: [
        { key: 'text', label: 'Point Text', type: 'text', placeholder: 'Communicative approach…' },
      ], defaultItems: [
        { text: 'Communicative approach focused on real-world use' },
        { text: 'Structured lessons with clear learning outcomes' },
        { text: 'Regular feedback and progress tracking' },
        { text: 'Adaptive content based on your interests and goals' },
      ]},
      { key: 'values', label: 'Core Values', type: 'repeater', itemFields: [
        { key: 'title',       label: 'Value Title',  type: 'text', placeholder: 'Authenticity' },
        { key: 'description', label: 'Description',  type: 'text', placeholder: 'Genuine connections…' },
      ], defaultItems: [
        { title: 'Authenticity',   description: 'Genuine connections and honest feedback' },
        { title: 'Empathy',        description: 'Understanding your unique learning journey' },
        { title: 'Confidence',     description: 'Building your ability to communicate freely' },
        { title: 'Excellence',     description: 'High standards in everything we do' },
        { title: 'Growth',         description: 'Continuous improvement and progress' },
        { title: 'Communication',  description: 'Clear, effective, and meaningful exchange' },
      ]},
      { key: 'cta', label: 'Bottom CTA', fields: [
        { key: 'title',         label: 'Title',            type: 'text',     placeholder: 'Ready to learn with confidence?', defaultValue: 'Ready to learn with [hl]confidence[/hl]?' },
        { key: 'body',          label: 'Description',      type: 'textarea',                                                   defaultValue: 'Start your journey with a trial lesson or discover your level with our free test.' },
        { key: 'cta_primary',   label: 'Primary Button',   type: 'text',     placeholder: 'Book a Trial Lesson',             defaultValue: 'Book a Trial Lesson' },
        { key: 'cta_secondary', label: 'Secondary Button', type: 'text',     placeholder: 'Test Your Level',                 defaultValue: 'Test Your Level' },
      ]},
    ]
  },
  {
    slug: 'courses', label: 'Courses Page', icon: BookOpen,
    sections: [
      { key: 'hero', label: 'Hero', fields: [
        { key: 'badge',  label: 'Badge',       type: 'text',     placeholder: 'Our Programs',                       defaultValue: 'Our Programs' },
        { key: 'title',  label: 'Title',       type: 'text',     placeholder: 'Courses Designed Around Real Goals', defaultValue: 'Courses Designed Around [hl]Real Goals[/hl]' },
        { key: 'body',   label: 'Description', type: 'textarea',                                                     defaultValue: 'Whether you want to improve your English for work, study, travel, or personal growth, our courses are personalized to help you move forward with confidence.' },
      ]},
      { key: 'courses', label: 'Course Cards', type: 'repeater', itemFields: [
        { key: 'title',       label: 'Course Title',              type: 'text',     placeholder: 'General English' },
        { key: 'subtitle',    label: 'Subtitle',                  type: 'text',     placeholder: 'From Beginners to Advanced' },
        { key: 'description', label: 'Description',               type: 'textarea' },
        { key: 'image',       label: 'Course Image',              type: 'image' },
        { key: 'badge',       label: 'Badge (e.g. Most Popular)', type: 'text' },
        { key: 'features',    label: 'Features (one per line)',    type: 'textarea', placeholder: 'All levels\nGrammar & vocabulary' },
        { key: 'button',      label: 'Button Text',               type: 'text',     placeholder: 'Book a Trial Lesson' },
      ], defaultItems: [
        { title: 'General English for Real Life',  subtitle: 'From Beginners to Advanced',    description: 'Speak English with confidence wherever life takes you. Our General English classes prepare you for travel, exchange programs, tourism, work opportunities, and building relationships in international and multicultural contexts. Less theory, more real communication. Get the English you actually need to live, connect, and succeed internationally.',  image: '/assets/photos/site-updates/in-loco-training-perfect-one.webp',                                                                                                                                              features: 'Handle airports, hotels, and travel situations with ease\nCommunicate professionally at work and in interviews\nConnect naturally on social media and in global environments\nBuild meaningful relationships with people from different cultures\nMake friends and navigate real-life conversations abroad', button: 'Book a Trial Lesson' },
        { title: 'Business English',               subtitle: 'Professional Communication',    description: 'Develop the language and communication skills you need for meetings, presentations, interviews, emails, negotiations, and international workplace interactions.',                                                                                         image: '/assets/photos/site-updates/business-communication-strategies.webp',                                                                                                                              features: 'Email writing\nPresentations\nNegotiations\nInterview prep',                         button: 'Book a Trial Lesson' },
        { title: 'English for Academic Purposes',  subtitle: 'Academic Success',              description: 'Prepare for academic success with lessons designed around essays, lectures, note-taking, academic reading, presentations, and effective study communication in English.',                                                                                 image: '/assets/photos/site-updates/perfect-academic-writing.webp',                                                                                                                                              features: 'Essay writing\nNote-taking\nResearch skills\nAcademic vocabulary',                   button: 'Book a Trial Lesson' },
        { title: 'Conversation Classes',           subtitle: 'Fluency & Confidence',          description: 'Practice real speaking with confidence through dynamic discussion-based lessons focused on fluency, pronunciation, spontaneity, and natural communication.',                                                                                             image: '/assets/photos/site-updates/working.webp',                                                                                                                                              features: 'Discussion topics\nPronunciation\nNatural flow\nConfidence building',               button: 'Book a Trial Lesson' },
        { title: 'Brazilian Portuguese',           subtitle: 'For Native English Speakers',   description: 'Learn Brazilian Portuguese in a natural, personalized, and confidence-building way with a native teacher who can help you speak clearly and comfortably.',                                                                                               image: '/assets/photos/site-updates/brasil.webp',                                                                                                                                                         features: 'Native teacher\nCultural context\nPractical phrases\nPersonalized pace',            button: 'Book a Trial Lesson' },
      ]},
      { key: 'cta', label: 'Bottom CTA', fields: [
        { key: 'title',         label: 'Title',            type: 'text',     placeholder: 'Not sure which course is right for you?', defaultValue: 'Not sure which course is right for you?' },
        { key: 'body',          label: 'Description',      type: 'textarea',                                                           defaultValue: 'Take the free level test or book a trial lesson and let us help you choose the best path.' },
        { key: 'cta_primary',   label: 'Primary Button',   type: 'text',     placeholder: 'Test Your Level',                          defaultValue: 'Test Your Level' },
        { key: 'cta_secondary', label: 'Secondary Button', type: 'text',     placeholder: 'Book a Trial',                             defaultValue: 'Book a Trial' },
      ]},
    ]
  },
  {
    slug: 'pricing', label: 'Pricing Page', icon: DollarSign,
    sections: [
      { key: 'hero', label: 'Hero', fields: [
        { key: 'badge',  label: 'Badge',       type: 'text',     placeholder: 'Pricing',             defaultValue: 'Pricing' },
        { key: 'title',  label: 'Title',       type: 'text',     placeholder: 'Choose Your Plan',    defaultValue: 'Choose Your [hl]Plan[/hl]' },
        { key: 'body',   label: 'Description', type: 'textarea',                                      defaultValue: 'Whether you prefer personalised attention or learning with others, we have the perfect option for you.' },
      ]},
      { key: 'packages', label: 'Pricing Plans', type: 'repeater', itemFields: [
        { key: 'name',        label: 'Plan Name',                    type: 'text',     placeholder: 'Private Class Pack' },
        { key: 'description', label: 'Short Description',            type: 'text',     placeholder: 'Best for fast individual progress' },
        { key: 'price',       label: 'Price Text',                   type: 'text',     placeholder: 'Contact for pricing' },
        { key: 'lessons',     label: 'Lessons',                      type: 'text',     placeholder: '10 lessons' },
        { key: 'duration',    label: 'Duration',                     type: 'text',     placeholder: '1 hour each' },
        { key: 'features',    label: 'Features (one per line)',       type: 'textarea', placeholder: 'Personalized support\nFlexible schedule' },
        { key: 'badge',       label: 'Badge (e.g. Most Popular)',    type: 'text' },
        { key: 'button',      label: 'Button Text',                  type: 'text',     placeholder: 'Get Started' },
        { key: 'button_link', label: 'Button Link',                  type: 'text',     placeholder: '/contact' },
      ], defaultItems: [
        { name: 'Private Classes (1:1)',     description: 'For fast progress and full personalisation',  price: '1 hour: 30€',              lessons: '10-class pack: 260€ (save 40€)', duration: 'Intensive (1.5h): 40€', features: 'Full personalised attention\nFlexible schedule\nGoogle Meet included\nProgress tracking\nCustom materials',                                  button: 'Get Started', button_link: '/contact' },
        { name: 'Pair Classes (2 students)',  description: 'Learn with a friend and stay motivated',       price: '1 hour: 22€ per student',    lessons: '10-class pack: 200€ (save 20€)', duration: 'Intensive (1.5h): 30€', features: 'Learn with a friend\nLower cost per student\nGoogle Meet included\nMore speaking time\nCollaborative learning',                           button: 'Get Started', button_link: '/contact' },
        { name: 'Trio Classes (3 students)', description: 'Dynamic, interactive, and affordable',        price: '1 hour: 17€ per student',    lessons: '10-class pack: 150€ (save 20€)', duration: 'Intensive (1.5h): 24€', features: 'Small group dynamic\nMore interaction\nGoogle Meet included\nPeer learning\nCost-effective',                                              button: 'Get Started', button_link: '/contact' },
        { name: 'Small Group (4–6 students)', description: 'Best value + real conversation practice',    price: '1 hour: 12€ per student',    lessons: '10-class pack: 110€ (save 10€)', duration: 'Intensive (1.5h): 18€', features: 'Best value option\nReal conversation practice\nGoogle Meet included\nCommunity atmosphere\nConsistent schedule', badge: 'Most Popular', button: 'Get Started', button_link: '/contact' },
      ]},
      { key: 'offers', label: 'Special Offers', type: 'repeater', itemFields: [
        { key: 'title',       label: 'Title',       type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ], defaultItems: [
        { title: 'Special Offer',  description: '20% discount for trio and small group students who purchase 10-class intensive packages.' },
        { title: 'Referral Bonus', description: 'Refer a friend and receive 10% off your next package renewal after they enrol.' },
      ]},
      { key: 'intensive_plan', label: 'Intensive Monthly Plan', fields: [
        { key: 'badge',         label: 'Badge',                type: 'text',     defaultValue: 'Want Faster Results?' },
        { key: 'title',         label: 'Title',                type: 'text',     defaultValue: 'Intensive Monthly Plan' },
        { key: 'subtitle',      label: 'Subtitle',             type: 'text',     defaultValue: '3 classes per week' },
        { key: 'group_price',   label: 'Group Price Text',     type: 'text',     defaultValue: 'From 320€/month (group)' },
        { key: 'private_price', label: 'Private Price Text',   type: 'text',     defaultValue: 'From 720€/month (private)' },
        { key: 'body',          label: 'Closing Description',  type: 'textarea', defaultValue: 'Perfect for students who want real progress in less time.' },
        { key: 'image_main',    label: 'Main Image',           type: 'image',    defaultValue: '/assets/photos/site-updates/flankly.webp' },
        { key: 'image_secondary', label: 'Secondary Image',    type: 'image',    defaultValue: '/assets/photos/site-updates/student-results-certificates.webp' },
      ]},
      { key: 'why_students', label: 'Why Students Choose Us', fields: [
        { key: 'badge', label: 'Badge', type: 'text', defaultValue: 'Why Students Choose Us' },
      ]},
      { key: 'why_students_items', label: 'Why Students — Items', type: 'repeater', itemFields: [
        { key: 'text', label: 'Item Text', type: 'text', placeholder: 'Practical English for real-life situations' },
      ], defaultItems: [
        { text: 'Practical English for real-life situations abroad' },
        { text: 'Small groups mean more speaking time' },
        { text: 'Flexible schedules adapted to your routine' },
        { text: 'Premium teaching with international qualifications' },
      ]},
      { key: 'cta', label: 'Bottom CTA', fields: [
        { key: 'title',         label: 'Title',            type: 'text',     defaultValue: 'Not sure which package is right for you?' },
        { key: 'body',          label: 'Description',      type: 'textarea', defaultValue: 'Take the free level test and get a recommended plan based on your level and goals.' },
        { key: 'cta_primary',   label: 'Primary Button',   type: 'text',     placeholder: 'Test Your Level', defaultValue: 'Test Your Level' },
        { key: 'cta_secondary', label: 'Secondary Button', type: 'text',     placeholder: 'Book a Trial',    defaultValue: 'Book a Trial' },
      ]},
    ]
  },
  {
    slug: 'testimonials', label: 'Testimonials Page', icon: Star,
    sections: [
      { key: 'hero', label: 'Hero', fields: [
        { key: 'badge',  label: 'Badge',       type: 'text',     placeholder: 'Testimonials',          defaultValue: 'Testimonials' },
        { key: 'title',  label: 'Title',       type: 'text',     placeholder: 'What Our Students Say', defaultValue: 'What Our Students Say' },
        { key: 'body',   label: 'Description', type: 'textarea',                                        defaultValue: 'Real stories. Real progress. Real confidence from learners around the world.' },
      ]},
      { key: 'cta', label: 'Bottom CTA', fields: [
        { key: 'title',         label: 'Title',            type: 'text',     defaultValue: 'Ready to Start Your Own [hl]Success Story[/hl]?' },
        { key: 'body',          label: 'Description',      type: 'textarea', defaultValue: 'Join our community of confident English speakers. Start with a free level test or book a trial lesson.' },
        { key: 'cta_primary',   label: 'Primary Button',   type: 'text',     placeholder: 'Test Your Level',   defaultValue: 'Test Your Level' },
        { key: 'cta_secondary', label: 'Secondary Button', type: 'text',     placeholder: 'Book a Trial',      defaultValue: 'Book a Trial' },
      ]},
    ]
  },
  {
    slug: 'faq', label: 'FAQ Page', icon: HelpCircle,
    sections: [
      { key: 'hero', label: 'Hero', fields: [
        { key: 'badge',  label: 'Badge',       type: 'text',     placeholder: 'Support',                    defaultValue: 'Support' },
        { key: 'title',  label: 'Title',       type: 'text',     placeholder: 'Frequently Asked Questions', defaultValue: 'Frequently Asked Questions' },
        { key: 'body',   label: 'Description', type: 'textarea',                                             defaultValue: 'Find answers to common questions about our courses, scheduling, and more.' },
      ]},
      { key: 'faq_items', label: 'FAQ Items', type: 'repeater', itemFields: [
        { key: 'question', label: 'Question', type: 'text',     placeholder: 'How do I book classes?' },
        { key: 'answer',   label: 'Answer',   type: 'textarea' },
      ], defaultItems: [
        { question: 'How do I book classes?',                   answer: 'Start with the free level test or a trial lesson. After we understand your goals and current level, we confirm the best package and available schedule with you directly.' },
        { question: 'Do you offer online lessons?',             answer: 'Yes. Online lessons are delivered live through Google Meet. All you need is a stable internet connection and a computer or tablet with a camera and microphone.' },
        { question: 'Do you offer in-person lessons?',          answer: 'Yes, depending on availability and location. Contact us to discuss in-person lesson options in your area.' },
        { question: 'How does the level test work?',            answer: 'Our level test is a quick online assessment that evaluates your grammar, vocabulary, reading, and listening skills. It takes about 5-10 minutes and gives you a practical understanding of your current English level, helping us recommend the best learning plan for you.' },
        { question: 'How do I choose the right package?',       answer: "We recommend taking the free level test first, which will help us understand your current level and suggest an appropriate package. You can also book a trial lesson where we can discuss your goals and recommend the best option for you." },
        { question: 'Can I reschedule lessons?',                answer: 'Yes. Lessons can be rescheduled when requested through the booking agenda at least 24 hours in advance. If the student does not notify us with 24 hours’ notice, the missed lesson will not be eligible for a refund or replacement class.' },
        { question: 'Do you teach children and teens?',         answer: 'We currently teach students aged 16 and above. At the moment, we do not offer classes for younger children.' },
        { question: 'Do you provide Business English?',         answer: 'Yes. We offer Business English classes focused on real professional communication, including meetings, presentations, emails, negotiations, and interviews. These classes can be tailored to your specific industry or profession.' },
        { question: 'What qualifications do your teachers have?', answer: "Our teacher is CELTA (Cambridge) certified, holds a Master’s degree in TESOL, and is currently pursuing a Doctorate focused on teaching English in the AI era, combining internationally recognized credentials with cutting-edge research in language education." },
        { question: 'How long does it take to see results?',    answer: 'Progress varies depending on your starting level, goals, and how much time you dedicate to learning. Most students notice improvements within the first few weeks, particularly in confidence and fluency.' },
        { question: 'What materials do you use?',               answer: 'We use a combination of high-quality course materials, custom resources, and real-world content tailored to your interests and goals. All materials are provided through Google Classroom.' },
        { question: 'How do I pay for lessons?',                answer: 'Payment options are confirmed during enrollment based on the selected package and location. Contact us and we will share the available payment methods and next steps.' },
      ]},
      { key: 'cta', label: 'Bottom CTA', fields: [
        { key: 'title',         label: 'Title',            type: 'text',     placeholder: 'Still have questions?', defaultValue: 'Still have questions?' },
        { key: 'body',          label: 'Description',      type: 'textarea',                                        defaultValue: "We're here to help. Get in touch and we'll answer any questions you have." },
        { key: 'cta_primary',   label: 'Primary Button',   type: 'text',     placeholder: 'Contact Us',            defaultValue: 'Contact Us' },
        { key: 'cta_secondary', label: 'Secondary Button', type: 'text',     placeholder: 'Book a Trial',          defaultValue: 'Book a Trial' },
      ]},
    ]
  },
  {
    slug: 'contact', label: 'Contact Page', icon: Phone,
    sections: [
      { key: 'hero', label: 'Hero', fields: [
        { key: 'badge',  label: 'Badge',       type: 'text',     placeholder: 'Get in Touch', defaultValue: 'Get in Touch' },
        { key: 'title',  label: 'Title',       type: 'text',     placeholder: "Let's Talk",   defaultValue: "Let's Talk" },
        { key: 'body',   label: 'Description', type: 'textarea',                               defaultValue: "Have questions or want help choosing the right course?\nGet in touch and we'll guide you." },
        { key: 'image',  label: 'Image',       type: 'image',                                    defaultValue: '/assets/photos/site-updates/professional-good-one.webp' },
      ]},
      { key: 'contact_info', label: 'Contact Info Cards', fields: [
        { key: 'phone_text',     label: 'Phone / WhatsApp Text', type: 'text',     defaultValue: 'Share your phone or WhatsApp in the form and we will contact you there.' },
        { key: 'location',       label: 'Location Text',         type: 'text',     defaultValue: 'Online Worldwide & In-Person Available' },
        { key: 'response_time',  label: 'Response Time Text',    type: 'text',     defaultValue: 'We reply within one business day' },
      ]},
      { key: 'cta_card', label: 'CTA Card', fields: [
        { key: 'title',  label: 'Title',       type: 'text',                                    defaultValue: 'Ready to Start Learning?' },
        { key: 'body',   label: 'Description', type: 'textarea',                                defaultValue: 'Book a trial lesson and experience our teaching approach firsthand.' },
        { key: 'button', label: 'Button Text', type: 'text',     placeholder: 'Book a Trial Lesson', defaultValue: 'Book a Trial Lesson' },
        { key: 'image',  label: 'Background Image', type: 'image', defaultValue: '/assets/photos/site-updates/modern-studio.webp' },
      ]},
    ]
  },
  {
    slug: 'booktrial', label: 'Book a Trial', icon: Calendar,
    sections: [
      { key: 'hero', label: 'Hero', fields: [
        { key: 'badge',  label: 'Badge',       type: 'text',     placeholder: 'Get Started',            defaultValue: 'Get Started' },
        { key: 'title',  label: 'Title',       type: 'text',     placeholder: 'Book Your Trial Lesson', defaultValue: 'Book Your [hl]Trial Lesson[/hl]' },
        { key: 'body',   label: 'Description', type: 'textarea',                                         defaultValue: 'Start with a first session designed to understand your level, your goals, and the best path for your learning journey.' },
        { key: 'image',  label: 'Image',       type: 'image',                                            defaultValue: '/assets/photos/site-updates/hero-final-touches.webp' },
      ]},
      { key: 'includes', label: 'Trial Includes — Header', fields: [
        { key: 'badge', label: 'Badge',       type: 'text',     defaultValue: 'Trial Benefits' },
        { key: 'title', label: 'Title',       type: 'text',     defaultValue: 'What Your Trial Includes' },
        { key: 'body',  label: 'Description', type: 'textarea', defaultValue: 'A comprehensive introduction to help you start your English journey with confidence.' },
      ]},
      { key: 'trial_includes', label: 'Trial Includes', type: 'repeater', itemFields: [
        { key: 'title',       label: 'Title',       type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ], defaultItems: [
        { title: 'Needs Assessment',       description: 'We discuss your goals, interests, and what you want to achieve with English.' },
        { title: 'Level Evaluation',       description: 'A friendly conversation to understand your current English level.' },
        { title: 'Personalized Plan',      description: 'Receive a recommended learning path tailored to your needs.' },
        { title: 'Teaching Style Preview', description: 'Experience our engaging and supportive teaching approach.' },
      ]},
      { key: 'next_steps', label: 'What Happens After — Header', fields: [
        { key: 'badge', label: 'Badge', type: 'text', defaultValue: 'Next Steps' },
        { key: 'title', label: 'Title', type: 'text', defaultValue: 'What Happens After Your Trial' },
      ]},
      { key: 'what_happens', label: 'What Happens After', type: 'repeater', itemFields: [
        { key: 'step',        label: 'Step Number', type: 'text',     placeholder: '1' },
        { key: 'title',       label: 'Title',       type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ], defaultItems: [
        { step: '1', title: 'Receive Your Assessment',   description: "You'll get feedback on your current level and areas for improvement." },
        { step: '2', title: 'Get Your Recommended Plan', description: "We'll suggest the best course and package based on your goals." },
        { step: '3', title: 'Choose Your Package',       description: 'Select the lesson package that fits your schedule and budget.' },
        { step: '4', title: 'Start Your Journey',        description: 'Begin regular lessons and track your progress towards confident English.' },
      ]},
      { key: 'bottom_cta', label: 'Bottom CTA', fields: [
        { key: 'body', label: 'Description', type: 'textarea', defaultValue: 'Not ready to book yet? Take our free level test first.' },
        { key: 'btn',  label: 'Button Text', type: 'text',     defaultValue: 'Take Free Level Test' },
      ]},
    ]
  },
  {
    slug: 'leveltest', label: 'Level Test', icon: GraduationCap,
    sections: [
      { key: 'hero', label: 'Intro Screen', fields: [
        { key: 'badge',   label: 'Badge',             type: 'text',     placeholder: 'Level Assessment',        defaultValue: 'Level Assessment' },
        { key: 'title',   label: 'Title',             type: 'text',     placeholder: 'Find Your English Level', defaultValue: 'Find Your [hl]English Level[/hl]' },
        { key: 'body',    label: 'Description',       type: 'textarea',                                          defaultValue: 'Take a quick assessment to understand your current level and receive a recommended learning plan based on your results.' },
        { key: 'button',  label: 'Start Button Text', type: 'text',     placeholder: 'Start Level Test',        defaultValue: 'Start Level Test' },
      ]},
      { key: 'email_step', label: 'Email Step', fields: [
        { key: 'badge',  label: 'Badge',       type: 'text',     placeholder: 'One Last Step',                      defaultValue: 'One Last Step' },
        { key: 'title',  label: 'Title',       type: 'text',     placeholder: 'See Your Result and Recommendation', defaultValue: 'See Your Result and Recommendation' },
        { key: 'body',   label: 'Description', type: 'textarea',                                                     defaultValue: 'Enter your email to unlock your level assessment, save your result, and receive the recommended plan for your English goals.' },
      ]},
    ]
  },
];

// ─── RECOMMENDED SIZE ────────────────────────────────────────────────────────
// Suggested source dimensions, matched to how each asset is rendered on the
// public site. An explicit per-field `hint` always wins; otherwise we infer a
// sensible default from the field's key/label.
const recommendedDimension = (field) => {
  if (field.hint) return field.hint;
  const ctx = `${field.key || ''} ${field.label || ''}`.toLowerCase();
  if (field.type === 'video')                          return '1920 × 1080 px · 16:9 (landscape)';
  if (ctx.includes('avatar'))                          return '256 × 256 px · 1:1 (square)';
  if (ctx.includes('poster') || ctx.includes('hero'))  return '1920 × 1080 px · 16:9 (landscape)';
  if (ctx.includes('cover') || ctx.includes('course')) return '1200 × 800 px · 3:2 (landscape)';
  return '1200 × 900 px · 4:3 (landscape)';
};

// ─── IMAGE FIELD ─────────────────────────────────────────────────────────────
const ImageField = ({ value, onChange, label, hint }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 1 * 1024 * 1024) {
      toast.error('Image must be smaller than 1 MB');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(res.data.url.startsWith('http') ? res.data.url : `${API_URL}${res.data.url}`);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-[#1A2E6E] mb-1">{label}</label>
      {value ? (
        <div className="relative w-fit max-w-full rounded-xl border border-[#E8EAF0] bg-[#F0F2FA] overflow-hidden">
          <img
            src={value}
            alt="preview"
            className="block max-w-full max-h-[220px] object-contain"
            onError={e => { e.target.style.display = 'none'; }}
          />
          <button
            onClick={() => onChange('')}
            className="absolute top-2 right-2 w-6 h-6 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80 transition-colors"
            title="Remove image"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-[#D1D5DB] bg-[#F8F9FC] flex flex-col items-center justify-center py-6 gap-2 text-[#1A2E6E]/40">
          <ImageIcon size={28} />
          <span className="text-xs">No image selected</span>
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="Paste image URL or upload →"
          className="flex-1 px-3 py-2 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1 px-3 py-2 border border-[#D1D5DB] rounded-xl text-sm text-[#1A2E6E] hover:bg-[#1A2E6E]/5 disabled:opacity-50 whitespace-nowrap"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      <p className="text-xs text-[#1A2E6E]/45 leading-relaxed">
        {hint && (
          <>
            <span className="font-medium text-[#1A2E6E]/70">Recommended: {hint}</span>
            <span className="mx-1.5 text-[#1A2E6E]/30">·</span>
          </>
        )}
        Max 1 MB · JPEG, PNG, GIF, WebP, SVG
      </p>
    </div>
  );
};

// ─── VIDEO FIELD ─────────────────────────────────────────────────────────────
const VideoField = ({ value, onChange, label, hint }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video must be smaller than 100 MB');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/media/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(res.data.url.startsWith('http') ? res.data.url : `${API_URL}${res.data.url}`);
      toast.success('Video uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-[#1A2E6E] mb-1">{label}</label>
      {value && (
        <div className="relative w-fit max-w-full rounded-xl border border-[#E8EAF0] bg-[#0B1220] overflow-hidden">
          <video
            src={value}
            controls
            className="block max-w-full max-h-[260px] object-contain"
          />
          <button
            onClick={() => onChange('')}
            className="absolute top-2 right-2 w-6 h-6 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70"
          >
            <X size={12} />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="Paste video URL or upload"
          className="flex-1 px-3 py-2 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1 px-3 py-2 border border-[#D1D5DB] rounded-xl text-sm text-[#1A2E6E] hover:bg-[#1A2E6E]/5 disabled:opacity-50"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Film size={14} />}
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="video/mp4,video/webm,video/ogg" className="hidden" onChange={handleFileChange} />
      <p className="text-xs text-[#1A2E6E]/45 leading-relaxed">
        {hint && (
          <>
            <span className="font-medium text-[#1A2E6E]/70">Recommended: {hint}</span>
            <span className="mx-1.5 text-[#1A2E6E]/30">·</span>
          </>
        )}
        Max 100 MB · MP4, WebM, OGG
      </p>
    </div>
  );
};

// ─── FIELD RENDERER ──────────────────────────────────────────────────────────
const FieldRenderer = ({ field, value, onChange }) => {
  if (field.type === 'image') {
    return <ImageField key={field.key} value={value} onChange={onChange} label={field.label} hint={recommendedDimension(field)} />;
  }
  if (field.type === 'video') {
    return <VideoField key={field.key} value={value} onChange={onChange} label={field.label} hint={recommendedDimension(field)} />;
  }
  if (field.type === 'toggle') {
    const isEnabled = value === true || value === 1 || value === '1' || value === 'true';
    return (
      <div key={field.key} className="rounded-xl border border-[#D1D5DB] px-4 py-3 flex items-center justify-between gap-4">
        <div>
          <label className="block text-sm font-medium text-[#1A2E6E]">{field.label}</label>
        </div>
        <button
          type="button"
          onClick={() => onChange(!isEnabled)}
          className={`relative h-7 w-12 rounded-full transition-colors ${
            isEnabled ? 'bg-[#1A2E6E]' : 'bg-[#D1D5DB]'
          }`}
          aria-pressed={isEnabled}
        >
          <span
            className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-transform ${
              isEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div key={field.key}>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1">{field.label}</label>
        <textarea
          rows={field.rows || 3}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
          className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none resize-none text-sm"
        />
      </div>
    );
  }
  return (
    <div key={field.key}>
      <label className="block text-sm font-medium text-[#1A2E6E] mb-1">{field.label}</label>
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={field.placeholder || ''}
        min={field.min}
        max={field.max}
        step={field.step}
        className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none text-sm"
      />
    </div>
  );
};

// ─── REPEATER SECTION ────────────────────────────────────────────────────────
// `activeLang === 'en'` edits the source list at content.items (unchanged
// behaviour). Any other language edits a full parallel list stored at
// content._translations[lang].items — repeaters translate as a whole list
// (not per-item-field) since item count/order can differ between languages.
// A first edit in a given language seeds that list from a clone of the
// current English items, so translators start from the right shape/order.
const RepeaterSection = ({ section, pageSlug, contentMap, setContentMap, onSave, saving, activeLang }) => {
  const mapKey = `${pageSlug}/${section.key}`;
  const savedContent = contentMap[mapKey];
  const enItems = savedContent === undefined
    ? (section.defaultItems || [])
    : (savedContent.items || []);
  const translatedItems = savedContent?._translations?.[activeLang]?.items;
  const items = activeLang === 'en'
    ? enItems
    : (Array.isArray(translatedItems) ? translatedItems : enItems.map(item => ({ ...item })));
  const [expandedIdx, setExpandedIdx] = useState(null);

  // When a repeater renders with defaults (never saved), materialize them into
  // contentMap so that "Save List" persists the defaults rather than empty.
  useEffect(() => {
    if (activeLang === 'en' && savedContent === undefined && section.defaultItems?.length > 0) {
      setContentMap(prev => ({
        ...prev,
        [mapKey]: { items: section.defaultItems },
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapKey, activeLang]);

  const setItems = (newItems) => {
    setContentMap(prev => {
      const current = prev[mapKey] || {};
      if (activeLang === 'en') {
        return { ...prev, [mapKey]: { ...current, items: newItems } };
      }
      const translations = { ...(current._translations || {}) };
      translations[activeLang] = { ...(translations[activeLang] || {}), items: newItems };
      return { ...prev, [mapKey]: { ...current, _translations: translations } };
    });
  };

  const handleAddItem = () => {
    const blank = {};
    section.itemFields.forEach(f => {
      blank[f.key] = f.type === 'toggle' ? false : '';
    });
    const next = [...items, blank];
    setItems(next);
    setExpandedIdx(next.length - 1);
  };

  const handleDelete = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
    if (expandedIdx === idx) setExpandedIdx(null);
  };

  const handleMoveUp = (idx) => {
    if (idx === 0) return;
    const next = [...items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setItems(next);
    setExpandedIdx(idx - 1);
  };

  const handleMoveDown = (idx) => {
    if (idx === items.length - 1) return;
    const next = [...items];
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    setItems(next);
    setExpandedIdx(idx + 1);
  };

  const handleFieldChange = (idx, fieldKey, value) => {
    const next = items.map((item, i) =>
      i === idx ? { ...item, [fieldKey]: value } : item
    );
    setItems(next);
  };

  const labelField = section.itemFields[0]?.key || 'title';

  return (
    <div className="space-y-2">
      {items.length === 0 && (
        <p className="text-sm text-[#1A2E6E]/40 italic py-2">No items yet. Click "Add Item" to start.</p>
      )}
      {items.map((item, idx) => (
        <div key={idx} className="border border-[#E8EAF0] rounded-xl overflow-hidden">
          <div
            className="flex items-center justify-between px-4 py-3 bg-[#F8F9FC] cursor-pointer hover:bg-[#F0F2F8]"
            onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs text-[#1A2E6E]/50 font-mono shrink-0">{idx + 1}</span>
              <span className="text-sm font-medium text-[#1A2E6E] truncate">
                {item[labelField] || `Item ${idx + 1}`}
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={e => { e.stopPropagation(); handleMoveUp(idx); }} disabled={idx === 0}
                className="p-1 text-[#1A2E6E]/40 hover:text-[#1A2E6E] disabled:opacity-20">
                <ChevronUp size={14} />
              </button>
              <button onClick={e => { e.stopPropagation(); handleMoveDown(idx); }} disabled={idx === items.length - 1}
                className="p-1 text-[#1A2E6E]/40 hover:text-[#1A2E6E] disabled:opacity-20">
                <ChevronDown size={14} />
              </button>
              <button onClick={e => { e.stopPropagation(); handleDelete(idx); }}
                className="p-1 text-red-400 hover:text-red-600">
                <Trash2 size={14} />
              </button>
              {expandedIdx === idx
                ? <ChevronDown size={16} className="text-[#1A2E6E]/40 ml-1" />
                : <ChevronRight size={16} className="text-[#1A2E6E]/40 ml-1" />
              }
            </div>
          </div>
          <AnimatePresence>
            {expandedIdx === idx && (
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-4 pt-3 space-y-3 border-t border-[#E8EAF0]">
                  {activeLang !== 'en' && (
                    <p className="text-xs text-[#1A2E6E]/45 italic">
                      Image/video fields are shared across languages — edit them from the English tab.
                    </p>
                  )}
                  {section.itemFields
                    .filter(field => activeLang === 'en' || (field.type !== 'image' && field.type !== 'video'))
                    .map(field => (
                      <FieldRenderer
                        key={field.key}
                        field={field}
                        value={item[field.key]}
                        onChange={val => handleFieldChange(idx, field.key, val)}
                      />
                    ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}

      <button
        onClick={handleAddItem}
        className="flex items-center gap-2 px-4 py-2 border border-dashed border-[#1A2E6E]/30 text-[#1A2E6E]/60 rounded-xl hover:border-[#1A2E6E] hover:text-[#1A2E6E] transition-colors text-sm w-full justify-center"
      >
        <Plus size={14} />
        Add Item
      </button>

      <div className="flex items-center justify-between pt-2">
        <p className="text-xs text-[#1A2E6E]/40">{items.length} item{items.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => onSave(pageSlug, section.key)}
          disabled={saving === `${pageSlug}/${section.key}`}
          className="flex items-center gap-2 px-5 py-2 bg-[#1A2E6E] text-white rounded-lg hover:bg-[#16275C] transition-colors text-sm font-medium disabled:opacity-50"
        >
          {saving === `${pageSlug}/${section.key}` ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save List
        </button>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export const AdminCMS = () => {
  const [contentMap, setContentMap] = useState({});
  const [loadingPages, setLoadingPages] = useState({});
  const [loadErrors, setLoadErrors] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [activePage, setActivePage] = useState(CMS_STRUCTURE[0].slug);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeLang, setActiveLang] = useState('en');

  const loadPageContent = useCallback(async (slug) => {
    let shouldLoad = false;
    setLoadingPages(prev => {
      if (prev[slug]) return prev;
      shouldLoad = true;
      return { ...prev, [slug]: true };
    });
    if (!shouldLoad) return;
    const endpoint = `${API_URL}/api/admin/content/${slug}`;
    setLoadErrors(prev => {
      const next = { ...prev };
      delete next[slug];
      return next;
    });
    try {
      if (!API_URL) {
        throw new Error('REACT_APP_BACKEND_URL is not configured');
      }
      const response = await axios.get(endpoint, { timeout: CONTENT_REQUEST_TIMEOUT_MS });
      if (!Array.isArray(response.data)) {
        throw new Error(`Unexpected response format: expected an array, received ${typeof response.data}`);
      }
      const map = {};
      for (const item of response.data) {
        map[`${slug}/${item.section_key}`] = item.content || {};
      }
      setContentMap(prev => ({ ...prev, ...map }));
    } catch (err) {
      const error = buildRequestError(err, endpoint, 'Failed to load content');
      setLoadErrors(prev => ({ ...prev, [slug]: error }));
      toast.error(error.message);
    } finally {
      setLoadingPages(prev => ({ ...prev, [slug]: false }));
    }
  }, []);

  useEffect(() => {
    loadPageContent(activePage);
  }, [activePage, loadPageContent]);

  const getFieldValue = (pageSlug, sectionKey, fieldKey, defaultValue = '') => {
    const savedContent = contentMap[`${pageSlug}/${sectionKey}`];
    if (savedContent === undefined) return defaultValue;
    const val = savedContent[fieldKey];
    return (val !== undefined && val !== '') ? val : defaultValue;
  };

  const setFieldValue = (pageSlug, sectionKey, fieldKey, value) => {
    const mapKey = `${pageSlug}/${sectionKey}`;
    setContentMap(prev => ({
      ...prev,
      [mapKey]: { ...(prev[mapKey] || {}), [fieldKey]: value }
    }));
  };

  // Language-aware field access: English reads/writes the top-level content
  // fields (unchanged); any other language reads/writes a parallel map at
  // content._translations[lang] so saving a translation never touches the
  // English source, and an untranslated field falls back to English on the
  // public site (see backend resolve_locale_content).
  const getLocalizedFieldValue = (pageSlug, sectionKey, fieldKey, defaultValue = '') => {
    if (activeLang === 'en') return getFieldValue(pageSlug, sectionKey, fieldKey, defaultValue);
    const savedContent = contentMap[`${pageSlug}/${sectionKey}`];
    const val = savedContent?._translations?.[activeLang]?.[fieldKey];
    return val !== undefined ? val : '';
  };

  const getFieldReferenceValue = (pageSlug, sectionKey, fieldKey, defaultValue = '') => {
    if (activeLang === 'en') return null;
    return getFieldValue(pageSlug, sectionKey, fieldKey, defaultValue) || null;
  };

  const setLocalizedFieldValue = (pageSlug, sectionKey, fieldKey, value) => {
    if (activeLang === 'en') return setFieldValue(pageSlug, sectionKey, fieldKey, value);
    const mapKey = `${pageSlug}/${sectionKey}`;
    setContentMap(prev => {
      const current = prev[mapKey] || {};
      const translations = { ...(current._translations || {}) };
      translations[activeLang] = { ...(translations[activeLang] || {}), [fieldKey]: value };
      return { ...prev, [mapKey]: { ...current, _translations: translations } };
    });
  };

  const handleSaveSection = async (pageSlug, sectionKey) => {
    const saveKey = `${pageSlug}/${sectionKey}`;
    const endpoint = `${API_URL}/api/admin/content/${pageSlug}/${sectionKey}`;
    const page = CMS_STRUCTURE.find(item => item.slug === pageSlug);
    const section = page?.sections?.find(item => item.key === sectionKey);
    setSavingKey(saveKey);
    try {
      if (!API_URL) {
        throw new Error('REACT_APP_BACKEND_URL is not configured');
      }
      const content = contentMap[saveKey] || getDefaultSectionContent(section);
      await axios.put(endpoint, { content }, { timeout: CONTENT_REQUEST_TIMEOUT_MS });
      clearCmsCache(pageSlug);
      toast.success('Section saved');
    } catch (err) {
      const error = buildRequestError(err, endpoint, 'Failed to save section');
      toast.error(error.message);
    } finally {
      setSavingKey(null);
    }
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const currentPage = CMS_STRUCTURE.find(p => p.slug === activePage);
  const activePageError = loadErrors[activePage];
  const activeSections = Array.isArray(currentPage?.sections) ? currentPage.sections : [];

  return (
    <div className="space-y-6" data-testid="admin-cms-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
          Content Management
        </h1>
        <p className="text-[#1A2E6E]/60 mt-1">
          Edit text, images, and lists across all public pages — no code required.
        </p>
      </div>

      {/* Language tabs */}
      <div className="flex flex-wrap items-center gap-2" data-testid="cms-language-tabs">
        <span className="text-xs font-semibold uppercase tracking-wide text-[#1A2E6E]/50 mr-1">Editing:</span>
        {SUPPORTED_LANGUAGES.map(lang => (
          <button
            key={lang.code}
            type="button"
            onClick={() => setActiveLang(lang.code)}
            data-testid={`cms-lang-${lang.code}`}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              activeLang === lang.code
                ? 'bg-[#1A2E6E] text-white'
                : 'bg-white text-[#1A2E6E] border border-[#E8EAF0] hover:bg-[#1A2E6E]/5'
            }`}
          >
            <span aria-hidden="true">{lang.flag}</span> {lang.label}
          </button>
        ))}
        {activeLang !== 'en' && (
          <span className="text-xs text-[#1A2E6E]/50 italic ml-1">
            Fields left blank fall back to the English content automatically.
          </span>
        )}
      </div>

      {/* Highlight syntax tip */}
      <div className="flex items-start gap-3 px-4 py-3 bg-[#F5A623]/8 border border-[#F5A623]/30 rounded-xl text-sm">
        <div>
          <span className="font-semibold text-[#1A2E6E]">Styled heading tip: </span>
          <span className="text-[#1A2E6E]/70">Wrap any word or phrase in a heading with </span>
          <code className="px-1.5 py-0.5 bg-white border border-[#E8EAF0] rounded text-[#D72B2B] font-mono text-xs">[hl]word[/hl]</code>
          <span className="text-[#1A2E6E]/70"> to apply a branded underline accent. Example: </span>
          <code className="px-1.5 py-0.5 bg-white border border-[#E8EAF0] rounded text-[#1A2E6E]/60 font-mono text-xs">Speak with [hl]Confidence[/hl]</code>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Page selector */}
        <div className="lg:w-56 shrink-0">
          <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden sticky top-4">
            <div className="px-4 py-3 border-b border-[#E8EAF0]">
              <p className="text-xs font-semibold text-[#1A2E6E]/50 uppercase tracking-wide">Pages</p>
            </div>
            {CMS_STRUCTURE.map(page => {
              const PageIcon = page.icon || FileText;
              return (
                <button
                  key={page.slug}
                  onClick={() => setActivePage(page.slug)}
                  className={`w-full flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b border-[#E8EAF0] last:border-0 ${
                    activePage === page.slug
                      ? 'bg-[#1A2E6E] text-white'
                      : 'text-[#1A2E6E] hover:bg-[#1A2E6E]/5'
                  }`}
                >
                  <PageIcon size={14} />
                  {page.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Sections editor */}
        <div className="flex-1 space-y-4 min-w-0">
          {loadingPages[activePage] && (
            <div className="flex items-center gap-3 rounded-xl border border-[#1A2E6E]/10 bg-white px-4 py-3 text-sm text-[#1A2E6E]/70">
              <Loader2 className="h-4 w-4 animate-spin text-[#D72B2B]" />
              Loading saved content. Default values remain editable while the request finishes.
            </div>
          )}

          {activePageError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-900">
              <p className="font-semibold mb-2">Content could not be loaded.</p>
              <p className="mb-2">{activePageError.message}</p>
              <p className="font-mono text-xs break-all text-red-800/80">{activePageError.endpoint}</p>
              <button
                type="button"
                onClick={() => loadPageContent(activePage)}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#1A2E6E] px-4 py-2 text-white transition-colors hover:bg-[#16275C]"
              >
                Retry
              </button>
            </div>
          )}

          {activeSections.map((section, idx) => {
              const sectionKey = `${activePage}/${section.key}`;
              const isExpanded = expandedSections[sectionKey] !== false; // default open

              return (
                <motion.div
                  key={section.key}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden"
                >
                  {/* Section header */}
                  <button
                    onClick={() => toggleSection(sectionKey)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#1A2E6E]/3 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#1A2E6E]">{section.label}</span>
                      {section.type === 'repeater' && (
                        <span className="text-xs bg-[#1A2E6E]/10 text-[#1A2E6E] px-2 py-0.5 rounded-full">List</span>
                      )}
                    </div>
                    {isExpanded
                      ? <ChevronDown size={18} className="text-[#1A2E6E]/40" />
                      : <ChevronRight size={18} className="text-[#1A2E6E]/40" />
                    }
                  </button>

                  {isExpanded && (
                    <div className="px-6 pb-6 border-t border-[#E8EAF0]">
                      {section.type === 'repeater' ? (
                        <div className="pt-4">
                          <RepeaterSection
                            section={section}
                            pageSlug={activePage}
                            contentMap={contentMap}
                            setContentMap={setContentMap}
                            onSave={handleSaveSection}
                            saving={savingKey}
                            activeLang={activeLang}
                          />
                        </div>
                      ) : (
                        <div className="pt-4 space-y-4">
                          {activeLang !== 'en' && (section.fields || []).some(f => f.type === 'image' || f.type === 'video') && (
                            <p className="text-xs text-[#1A2E6E]/45 italic">
                              Image/video fields are shared across languages — edit them from the English tab.
                            </p>
                          )}
                          {(section.fields || [])
                            .filter(field => activeLang === 'en' || (field.type !== 'image' && field.type !== 'video'))
                            .map(field => {
                              const referenceValue = getFieldReferenceValue(activePage, section.key, field.key, field.defaultValue || '');
                              return (
                                <FieldRenderer
                                  key={field.key}
                                  field={referenceValue ? { ...field, placeholder: referenceValue } : field}
                                  value={getLocalizedFieldValue(activePage, section.key, field.key, field.defaultValue || '')}
                                  onChange={val => setLocalizedFieldValue(activePage, section.key, field.key, val)}
                                />
                              );
                            })}
                          <div className="flex items-center justify-between pt-2">
                            <p className="text-xs text-[#1A2E6E]/40">Changes are saved per section</p>
                            <button
                              onClick={() => handleSaveSection(activePage, section.key)}
                              disabled={savingKey === sectionKey}
                              className="flex items-center gap-2 px-5 py-2 bg-[#1A2E6E] text-white rounded-lg hover:bg-[#16275C] transition-colors text-sm font-medium disabled:opacity-50"
                            >
                              {savingKey === sectionKey
                                ? <Loader2 size={14} className="animate-spin" />
                                : <Save size={14} />
                              }
                              Save Section
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}

          <div className="bg-[#F5A623]/5 border border-[#F5A623]/20 rounded-xl p-4 text-sm text-[#1A2E6E]/70">
            <strong className="text-[#1A2E6E]">Tip:</strong> Content updates are live immediately after saving.
            Images can be uploaded (max 1 MB) or linked via URL. List items support drag-reorder via the arrow buttons.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCMS;
